import React, { memo, useState, useCallback } from 'react';
import { areEqual } from 'Utils/equalityChecks';

import { JobCostingProfitability } from './JobCostingProfitability';
import { JobCostingDetail } from './JobCostingDetail';
import { JobCostingCategories } from './JobCostingCategories';
import { JobCostingUnassigned } from './JobCostingUnassigned';
import type { JobCostingProps } from './types';
import classes from './jobCosting.module.css';

// Unassigned-costs is empty until the QBO cost sync lands on the backend. Built but
// gated so we don't ship a permanently-empty tab (flip when sync is live). See
// WEBAPP-FR-003.
const SHOW_UNASSIGNED_COSTS = false;

type Tab = 'profitability' | 'categories' | 'unassigned';

interface SelectedJob {
  opportunityId: string;
  name: string | null;
}

function JobCostingContainer({ locationId, ssoToken }: JobCostingProps) {
  const [activeTab, setActiveTab] = useState<Tab>('profitability');
  const [selectedJob, setSelectedJob] = useState<SelectedJob | null>(null);

  const handleSelectJob = useCallback((opportunityId: string, name: string | null) => {
    setSelectedJob({ opportunityId, name });
  }, []);

  const handleBack = useCallback(() => setSelectedJob(null), []);

  const handleTab = useCallback((tab: Tab) => {
    setSelectedJob(null);
    setActiveTab(tab);
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.tabNav}>
        <button
          type="button"
          className={`${classes.tab} ${activeTab === 'profitability' ? classes.tabActive : ''}`}
          onClick={() => handleTab('profitability')}
        >
          Profitability
        </button>
        <button
          type="button"
          className={`${classes.tab} ${activeTab === 'categories' ? classes.tabActive : ''}`}
          onClick={() => handleTab('categories')}
        >
          Category Mapping
        </button>
        {SHOW_UNASSIGNED_COSTS && (
          <button
            type="button"
            className={`${classes.tab} ${activeTab === 'unassigned' ? classes.tabActive : ''}`}
            onClick={() => handleTab('unassigned')}
          >
            Unassigned Costs
          </button>
        )}
      </div>

      {activeTab === 'profitability' &&
        (selectedJob ? (
          <JobCostingDetail
            ssoToken={ssoToken}
            locationId={locationId}
            opportunityId={selectedJob.opportunityId}
            jobName={selectedJob.name}
            onBack={handleBack}
          />
        ) : (
          <JobCostingProfitability
            ssoToken={ssoToken}
            locationId={locationId}
            onSelectJob={handleSelectJob}
          />
        ))}

      {activeTab === 'categories' && <JobCostingCategories ssoToken={ssoToken} />}

      {SHOW_UNASSIGNED_COSTS && activeTab === 'unassigned' && (
        <JobCostingUnassigned ssoToken={ssoToken} />
      )}
    </div>
  );
}

const JobCostingContainerMemo = memo(JobCostingContainer, areEqual);

export { JobCostingContainerMemo as JobCostingContainer };
